import type React from "react";
import "./CurrentTenantPaymentList.css";
import {
  ContractorService,
  EntityType,
  PaymentDueAccountVM,
  PaymentMethodTypesEnum,
  PaymentMethodVM,
  PaymentService,
} from "@propertelligent/client-api";
import apiHelper from "../../utils/apiHelper";
import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import deleteIcon from "../../assets/componentsIcons/dropdown/delete.svg";
import dropdownIcon from "../../assets/componentsIcons/dropdown/dropdown.svg";
import { ConfirmationPopup } from "../confirmation-popup/confirmation-popup-demo";
import CurrencyTooltip from "../../components/currencytooltip/CurrencyTooltip";
import RightSideModal from "../Modal/right-side-modal";
import Card from "../card";
import TruncatedDescriptionCell from "../common/TruncatedDescriptionCell";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import SelectField from "../form-fields/SelectField";
import InputField from "../form-fields/InputField";
import { UserLoginData } from "../../components/common/charts/Enums";
import ActionMenu from "../actionMenus/action-menu";
import { utcDateFormat } from "../../utils/helper";
import DateHelper from "../../utils/DateHelper";
import UtilHelper from "../../utils/UtilHelper";
import H2 from "../typography/H2";
import SimpleSpinner from "../../components/loader";
import { useCurrencySymbol } from "../../hooks/useCurrencySymbol";
import {
  usePaymentSubmitGuard,
  PayPaymentDueAccountWithKey,
  NextIdempotencyKey,
} from "../../hooks/usePaymentSubmitGuard";
import { NotificationType, PubSub, TopicsEnum } from "../../utils/pubsub";
import { GetImages } from "../../utils/GetImages";
import EmptyState from "../emptystate";
import BluePayableIcon from "../../assets/Payment-Icon.svg";
import { getEnumOptions, getTransferFailureReasonLabel } from "../../utils/enumHelpers";
import StatusTag from "../../components/common/StatusTag";

// Recording modes — same set as Service Request / Payables post (no Assisted).
const PAYMENT_MODE = PaymentMethodTypesEnum;
const PAYMENT_MODE_OPTIONS = getEnumOptions(PAYMENT_MODE, [
  PAYMENT_MODE.Plaid,
  PAYMENT_MODE.Cash,
  PAYMENT_MODE.Check,
  PAYMENT_MODE.Other,
]);

// @propertelligent/client-api -- putApiPaymentDueAccountPay's generated return type still says
// StringSingleItemResponseVM (bare `success: string`) as of this change, but the backend track of
// this same fix (bug/plaid-optimistic-paid) changes the endpoint's actual runtime response shape
// to `{ isPending, message }`. This interim local type mirrors that documented API Contract
// exactly so the UI can branch on the real field instead of parsing the message string -- same
// interim-local-type idiom already used in ServiceRequestDetails.tsx. Drop this once the client
// republishes and read the generated type directly.
interface PayPaymentDueAccountResultVM {
  isPending: boolean;
  message: string;
}

type PutPaymentResponseSuccess = PayPaymentDueAccountResultVM | string | undefined;

// Reads the real backend response instead of trusting the call merely resolved -- handles both
// the pre-rollout bare-string shape and the post-rollout { isPending, message } shape so this
// keeps working through the rollout (see bug/plaid-optimistic-paid).
const readPaymentResult = (
  success: PutPaymentResponseSuccess,
): { isPending: boolean; message: string } => {
  if (success && typeof success === "object") {
    return {
      isPending: success.isPending === true,
      message: success.message || "Payment posted successfully!",
    };
  }
  return { isPending: false, message: (success as string) || "Payment posted successfully!" };
};

export interface Payment {
  id?: number;
  payerPayeeName?: string | null;
  description?: string | null;
  dueDate?: string;
  amount?: number;
  streetAddress?: string | null;
  isEntity?: boolean;
  entityId?: number;
  entityTypeId?: number;
  className?: string;
  detailsActiveTab?: string;

  refreshTrigger?: number;
  onRefresh?: () => void;

  expensesDueRaw?: number | string;
  expensesDue?: number | string;
}

const ContractorPaymentList: React.FC<Payment> = ({
  isEntity,
  entityId,
  entityTypeId,
  className,
  detailsActiveTab,
  refreshTrigger,
  onRefresh,
  expensesDue,
  expensesDueRaw
}) => {
  const { id: contractorId } = useParams();
  const [payments, setPayments] = useState<PaymentDueAccountVM[]>([]);
  const [plaidPaymentMethods, setPlaidPaymentMethods] = useState<
    { Id: string; Name: string }[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { isSubmitting: isPaySubmitting, runGuarded: runPayGuarded } =
    usePaymentSubmitGuard();
  const [showConfirmation, setShowConfirmation] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [contractorData, setContractorData] = useState<any>(null);
  const [selectedPayment, setSelectedPayment] = useState<PaymentDueAccountVM | null>(null);
  const [isBulk, setIsBulk] = useState(false);

  const currencySymbol = useCurrencySymbol();
  const methods = useForm({
    mode: "onChange",
    defaultValues: {
      paymentMethodTypeId: "",
      mode: "",
      date: new Date().toISOString().split("T")[0],
      checkNo: "",
      transactionId: "",
    },
  });
  const navigate = useNavigate();
  const formId = "post-payment-form";

  const selectedPaymentModeType = Number(
    useWatch({ control: methods.control, name: "paymentMethodTypeId" }),
  );
  const isCheck = selectedPaymentModeType === PAYMENT_MODE.Check;
  const showTransactionId = selectedPaymentModeType === PAYMENT_MODE.Other;

  const {
    formState: { isDirty, isSubmitting, isValid },
  } = methods;

  // Menu actions — Post always; Delete only for non–service-request dues
  // (SR-linked PDAs must be settled from the service request — bug/sr-pda-undeletable).
  const getMenuItems = (payment: PaymentDueAccountVM) => {
    const postItem = {
      icon: BluePayableIcon,
      label: "Post Payment",
      action: () => {
        setSelectedPayment(payment);
        setModalOpen(true);
      },
      isDivider: false,
      isDestructive: false
    };

    if (payment.entityTypeId === EntityType.ServiceRequest) {
      return [postItem];
    }

    return [
      postItem,
      { isDivider: true },
      {
        icon: deleteIcon,
        label: "Delete",
        action: () => {
          setSelectedPayment(payment);
          setShowConfirmation("delete");
        },
        isDivider: false,
        isDestructive: false
      },
    ];
  };

  // Reset conditional fields when payment mode changes
  useEffect(() => {
    methods.resetField("checkNo");
    methods.resetField("transactionId");
    methods.resetField("mode");
  }, [selectedPaymentModeType]);

  const deletePayment = async () => {
    if (!selectedPayment?.id || isDeleting) return;

    setIsDeleting(true);
    try {
      await apiHelper(
        PaymentService.deleteApiPaymentDueAccount,
        {
          successIcon: <img src={GetImages.toastPaymentAccountIcon} alt="success" width="20" height="20" />,
          showNotification: true,
          successMessage: "Payment Deleted Successfully",
        },
        selectedPayment.id
      );
      getPayments();
      setShowConfirmation(null);
      setSelectedPayment(null);

      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Delete Payment Error:", err);
      setShowConfirmation(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const fetchCurrentContractorDetails = () => {
    if (contractorId) {
      apiHelper(ContractorService.getApiContractor, { showNotification: false }, contractorId)
        .then((res) => {
          if (res?.data?.success) {
            setContractorData(res.data.success);
          }
        })
        .catch((err) => {
          console.error("Error fetching contractor:", err);
        });
    }
  };

  const getPaymentsMode = async () => {
    try {
      const orgId = parseInt(
        sessionStorage.getItem(UserLoginData.organization_id) || "0"
      );
      const res = await apiHelper(
        PaymentService.getApiPaymentMethodOrganizationlist,
        { showNotification: false },
        orgId
      );
      const rawItems: PaymentMethodVM[] = res?.data?.success || [];
      // Secondary Plaid account picker only — recording modes come from the enum
      // (Cash/Check/Other/Plaid), matching Service Request / Payables post.
      setPlaidPaymentMethods(
        rawItems
          .filter((item) => item.paymentMethodTypeId === PAYMENT_MODE.Plaid)
          .map((item) => ({
            Id: String(item.id),
            Name: item.accountName || `Account ${item.id}`,
          })),
      );
    } catch (err) {
      console.error("Payments API Error:", err);
      setPlaidPaymentMethods([]);
    }
  };

  const getPayments = async () => {
    if (!contractorId) return;
    setLoading(true);
    try {
      const res = await apiHelper(
        PaymentService.getApiPaymentDueAccountContractorlist,
        { showNotification: false },
        contractorId
      );
      const rawItems = res?.data?.success || [];



      if (rawItems.length) {
        setPayments(rawItems);



      } else {
        setPayments([]);
      }
    } catch (err) {
      console.error("Contractor Payments API Error:", err);
    } finally {
      setLoading(false);
    }
  };


  const handleSinglePaymentOpen = (payment: PaymentDueAccountVM) => {
    setIsBulk(false);
    setSelectedPayment(payment);
    setModalOpen(true);
  };

  const getNumericAmount = (amount: any): number => {
    if (typeof amount === 'number') return amount;
    if (typeof amount === 'string') {
      // Remove currency symbols and commas, then parse
      return parseFloat(amount.replace(/[^0-9.-]+/g, "")) || 0;
    }
    return 0;
  };

  const postPaymentsWithKeys = async (
    formData: any,
    nextKey: NextIdempotencyKey,
  ) => {
    try {
      const paymentDate = utcDateFormat(formData.date);
      const paymentMethodTypeId = Number(formData.paymentMethodTypeId);

      const buildPayload = (payDueAccountId: number, amount: number | undefined) => {
        const payload: PayPaymentDueAccountWithKey = {
          payDueAccountId,
          amount,
          paymentMethodTypeId,
          paymentDate,
          idempotencyKey: nextKey(),
        };
        if (paymentMethodTypeId === PAYMENT_MODE.Plaid) {
          payload.fromPaymentMethodId = Number(formData.mode);
        } else if (paymentMethodTypeId === PAYMENT_MODE.Check) {
          payload.checkNumber = formData.checkNo || "";
        } else if (paymentMethodTypeId === PAYMENT_MODE.Other) {
          payload.transactionId = formData.transactionId || "";
        }
        return payload;
      };

      if (isBulk) {
        // Aggregate each individual /pay call's real outcome instead of discarding it, so the one
        // summary toast below honestly reflects how many payments are still pending bank
        // confirmation (Plaid/Default) vs. genuinely completed vs. failed -- see
        // bug/plaid-optimistic-paid. Mirrors the shape (not the code) of PayGroupAsync's own
        // paid/pending/skipped count composition; this loop still calls the single-item endpoint
        // N times, not /pay/group (out of scope for this fix).
        let completedCount = 0;
        let pendingCount = 0;
        let failedCount = 0;

        for (const payment of payments) {
          try {
            const response = await apiHelper(
              PaymentService.putApiPaymentDueAccountPay,
              { showNotification: false },
              buildPayload(Number(payment.id), payment.amount),
            );
            // apiHelper never throws (it swallows the error into `response.data === null`), so a
            // genuine failure must be detected by checking for a missing `data.success`, not by
            // relying on this try/catch's `catch` branch -- a bare response-truthiness check here
            // would otherwise silently count every failed item as "recorded successfully".
            if (!response?.data?.success) {
              failedCount += 1;
              continue;
            }
            const { isPending } = readPaymentResult(
              response.data.success as PutPaymentResponseSuccess,
            );
            if (isPending) {
              pendingCount += 1;
            } else {
              completedCount += 1;
            }
          } catch (err) {
            console.error("Post Payment Error (bulk item):", err);
            failedCount += 1;
          }
        }

        const summaryParts: string[] = [];
        if (completedCount > 0) {
          summaryParts.push(
            `${completedCount} payment${completedCount === 1 ? "" : "s"} recorded successfully.`,
          );
        }
        if (pendingCount > 0) {
          summaryParts.push(
            `${pendingCount} payment${pendingCount === 1 ? "" : "s"} submitted — awaiting bank confirmation.`,
          );
        }
        if (failedCount > 0) {
          summaryParts.push(
            `${failedCount} payment${failedCount === 1 ? "" : "s"} could not be processed.`,
          );
        }

        PubSub.publish(TopicsEnum.Notification.toString(), {
          message: summaryParts.join(" ") || "No payments were processed.",
          type: failedCount > 0 ? NotificationType.Failure : NotificationType.Success,
          icon: <img src={GetImages.toastPaymentAccountIcon} alt="success" width="20" height="20" />,
        });
      } else {
        if (!selectedPayment) return;
        await apiHelper(
          PaymentService.putApiPaymentDueAccountPay,
          {
            showNotification: true,
            // Render the actual API response message (e.g. the pending-confirmation wording for a
            // Plaid transfer that hasn't cleared yet) instead of an unconditional hardcoded string
            // -- see bug/plaid-optimistic-paid.
            successMessage: (res: { success?: PutPaymentResponseSuccess }) =>
              readPaymentResult(res?.success).message,
            successIcon: <img src={GetImages.toastPaymentAccountIcon} alt="success" width="20" height="20" />,
          },
          buildPayload(Number(selectedPayment.id), selectedPayment.amount),
        );
      }

      getPayments();
      setModalOpen(false);
      setIsBulk(false);
      setSelectedPayment(null);
      methods.reset();

      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Post Payment Error:", err);
    }
  };



  const postPayments = (formData: any) =>
    runPayGuarded((nextKey) => postPaymentsWithKeys(formData, nextKey));

  useEffect(() => {
    if (contractorId) {
      getPayments();
      getPaymentsMode();
      fetchCurrentContractorDetails();
    }
  }, [contractorId]);

  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      getPayments();
    }
  }, [refreshTrigger]);


 if (loading) {
    return (
     <SimpleSpinner myClassName={"Icon50"} isFullPage={true} />
    );
  }

  if (payments.length === 0) {
    return (
      <div className="content-wrapper container-fluid">
        <EmptyState
          icon={GetImages.emptyStatePayment}
          heading="No Payments Found"
          isButton={false}
        />
      </div>

    );
  }

  return (
    <>
        <div className="payments-content">
          <div className="payments-header">
            <H2
              text="Payments"
            />
            {/* <SimpleButton
              label="Add Payment"
              onClick={() => {
                navigate("/payment/create", {
                  state: {
                    isEntityProp: isEntity,
                    entityIdProp: entityId,
                    entityTypeIdProp: entityTypeId,
                    from: window.location.pathname,
                    activeTab: detailsActiveTab,
                  },
                });
              }}
            /> */}
          </div>
          <div className="table-container">

            <table className="payments-table">
              <thead>
                <tr>
                  <th>Address</th>
                  <th>Description</th>
                  <th>Due Date</th>
                  <th>Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {payments.length ? (
                  payments.map((payment) => {
                    return (
                    <tr key={payment.id}>
                      <td>{payment.streetAddress || "-"}</td>
                      <td>
                        <TruncatedDescriptionCell description={payment.description} />
                      </td>
                      <td>
                        {DateHelper.formatISOToMMDDYYYY(payment.dueDate) || "-"}
                      </td>
                      <td>
                        <CurrencyTooltip
                          content={payment.formatted?.AmountRaw || 0}
                          formatted={payment.formatted?.Amount}
                        >
                          <span>
                            {payment.formatted?.Amount ||
                              "-"}
                          </span>
                        </CurrencyTooltip>
                      </td>
                      <td>
                        <ActionMenu
                          menuItems={getMenuItems(payment)}
                          trigger={
                            <button
                              className="action-button"
                              aria-label="More options"
                            >
                              <img
                                src={dropdownIcon}
                                alt="More options"
                                width={20}
                                height={20}
                                className="action-icon"
                              />
                            </button>
                          }
                        />
                      </td>
                    </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center" }}>
                      No payments found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

          </div>

          {
            payments.length > 0 && (
              <div className="total-amount-card">
                <div className="total-amount-content" style={{ justifyContent: "space-between" }}>
                  <div>
                    <span className="total-label">Total Amount Due</span>
                  </div>



                  <CurrencyTooltip
                    content={expensesDueRaw}
                    formatted={expensesDue}
                  >
                    <span className="total-value">{expensesDue}</span>
                  </CurrencyTooltip>
                </div>
              </div>
            )
          }


        </div>
     

      {/* Delete Confirmation */}
      {showConfirmation === "delete" && selectedPayment && (
        <ConfirmationPopup
          isOpen={true}
          title="Delete Payment"
          message={`Do you want to delete this payment of ${currencySymbol}${selectedPayment?.amount?.toFixed(2)}?`}
          primaryButtonLabel="Delete"
          secondaryButtonLabel="Cancel"
          onPrimaryAction={deletePayment}
          onSecondaryAction={() => setShowConfirmation(null)}
          onClose={() => setShowConfirmation(null)}
          isPrimaryDestructive={true}
          isLoading={isDeleting}
          loadingLabel="Deleting..."
        />
      )}

      {/* Post Payment Modal */}
      {isModalOpen && (
        <RightSideModal
          isOpen={isModalOpen}
          onClose={() => { setModalOpen(false); setIsBulk(false); }}
          title={isBulk ? "Post All Payments" : "Post Payment"}
          buttonlabel="Save"
          formId={formId}
          maxWidth={550}
          isLoading={isSubmitting || isPaySubmitting}
          isDirty={isDirty}
          isSubmitDisabled={isSubmitting || isPaySubmitting || !isDirty || !isValid}
        >
          <Card className="no-bg">




            <div className="tenant-info">
              {contractorData && selectedPayment?.amount && (
                <>
                  <div className="tenant-left">
                    <div className="tenant-avatar">
                      <div className="tenant-initials">
                        {UtilHelper.getInitials(
                          contractorData?.firstName + " " + contractorData?.lastName
                        )}
                      </div>
                    </div>
                    <div className="tenant-name">
                      {contractorData?.firstName + " " + contractorData?.lastName}
                      <p className="tenant-payment-type">  {isBulk ? `Contractor Payments` : "Contractor Payment"}</p>
                    </div>
                  </div>
                  <div className="tenant-amount">
                    {currencySymbol}{getNumericAmount(selectedPayment?.amount).toFixed(2)}
                  </div>
                </>
              )}
            </div>

            {!isBulk && selectedPayment?.transferFailed && (
              <div className="payment-status-banner">
                <StatusTag
                  color="red"
                  statusText={
                    getTransferFailureReasonLabel(selectedPayment?.transferFailureReason)
                      ? `Payment attempt failed — ${getTransferFailureReasonLabel(selectedPayment?.transferFailureReason)}`
                      : "Payment attempt failed"
                  }
                  isLarge={false}
                  isLong={true}
                />
              </div>
            )}
            {!isBulk && !selectedPayment?.transferFailed && selectedPayment?.autoPayTransferPending && (
              <div className="payment-status-banner">
                <StatusTag
                  color="blue"
                  statusText="Payment submitted — awaiting bank confirmation"
                  isLarge={false}
                  isLong={true}
                />
              </div>
            )}

            <FormProvider {...methods}>
              <form id={formId} onSubmit={methods.handleSubmit(postPayments)}>
                <div className="row">
                  <div className="col-md-6">
                    <SelectField
                      name="paymentMethodTypeId"
                      label="Payment Mode"
                      required
                      requiredMessage="Payment mode is required"
                      options={PAYMENT_MODE_OPTIONS}
                      placeholderText="Select Mode"
                    />
                  </div>
                  <div className="col-md-6">
                    <InputField name="date" label="Payment Date" type="date" required requiredMessage="Payment Date is required" />
                  </div>
                </div>

                {selectedPaymentModeType === PAYMENT_MODE.Plaid && (
                  <SelectField
                    name="mode"
                    label="Payment Method"
                    required
                    requiredMessage="Please select a payment method"
                    options={plaidPaymentMethods}
                    placeholderText="Select a payment method"
                  />
                )}

                {isCheck && (
                  <InputField
                    name="checkNo"
                    label="Enter Check No"
                    type="text"
                    required={true}
                    requiredMessage="Please enter a check number"
                    placeholder="Enter check number"
                    onlyNumeric={true}
                  />
                )}

                {showTransactionId && (
                  <InputField
                    name="transactionId"
                    label="Transaction ID"
                    type="text"
                    required={true}
                    requiredMessage="Please enter a transaction ID"
                    placeholder="Enter transaction ID"
                  />
                )}
              </form>
              <div className="payment-alert">
                <p>Payment Amount:</p>
                <p className="payment-divider">------------------------</p>
                <p style={{ fontWeight: "bold" }}>
                  {currencySymbol}{getNumericAmount(selectedPayment?.amount).toFixed(2)}
                </p>
              </div>
            </FormProvider>
          </Card>
        </RightSideModal>
      )}
    </>
  );
};

export default ContractorPaymentList;